import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../../utils/api';
import './WheelchairPage.css';

const WheelchairPage = () => {
  const navigate = useNavigate();
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [deliveryType, setDeliveryType] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [orderHistory, setOrderHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [currentSubscriptions, setCurrentSubscriptions] = useState([]);
  const [showCurrentSubscriptions, setShowCurrentSubscriptions] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const saved = localStorage.getItem('wheelchair-theme');
    return saved === 'dark' || (!saved && window.matchMedia('(prefers-color-scheme: dark)').matches);
  });
  
  // Address form state
  const [addressData, setAddressData] = useState({
    street: '',
    city: '',
    state: '',
    zipCode: '',
    country: 'Bangladesh',
    phone: '',
    notes: ''
  });

  const subscriptionPlans = [
    {
      id: 'daily',
      name: 'Daily Subscription',
      duration: '24 Hours',
      price: 1000,
      urgentPrice: 1500,
      originalPrice: 1000,
      originalUrgentPrice: 1500,
      discount: 0,
      features: [
        '24 hours of uninterrupted usage',
        'Easy delivery and pickup',
        'Perfect for short-term needs',
        'Same-day delivery available',
        'Professional wheelchair maintenance',
        'Emergency replacement service',
        '24/7 customer support'
      ],
      icon: '🦽',
      color: 'blue',
      popular: false
    },
    {
      id: 'weekly',
      name: 'Weekly Subscription',
      duration: '7 Days',
      price: 6160,
      urgentPrice: 9240,
      originalPrice: 7000,
      originalUrgentPrice: 10500,
      discount: 12,
      features: [
        '7 days of uninterrupted service',
        'Budget-friendly option',
        'Easy delivery and pickup',
        'Less hassle - order once for the week',
        'Suitable for sudden accidents or injuries',
        'Priority customer support',
        'Flexible delivery scheduling'
      ],
      icon: '🦽',
      color: 'green',
      popular: true
    },
    {
      id: 'monthly',
      name: 'Monthly Subscription',
      duration: '30 Days',
      price: 22500,
      urgentPrice: 33750,
      originalPrice: 30000,
      originalUrgentPrice: 45000,
      discount: 25,
      features: [
        '30 days of uninterrupted service',
        '24/7 customer service for any difficulties',
        'Free delivery and pickup',
        'No hassle for a month',
        'Best value for money',
        'Priority maintenance service',
        'Emergency replacement guarantee',
        'Flexible scheduling options'
      ],
      icon: '🦽',
      color: 'purple',
      popular: false
    }
  ];

  const deliveryOptions = [
    {
      id: 'standard',
      name: 'Standard Delivery',
      description: 'Regular delivery within 2-4 hours',
      price: 0,
      icon: '🚚'
    },
    {
      id: 'urgent',
      name: 'Urgent Delivery',
      description: 'Express delivery within 30-60 minutes',
      price: 500,
      icon: '⚡'
    }
  ];

  useEffect(() => {
    fetchOrderHistory();
    fetchCurrentSubscriptions();
  }, []);

  // Dark mode initialization and persistence
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', isDarkMode ? 'dark' : 'light');
    localStorage.setItem('wheelchair-theme', isDarkMode ? 'dark' : 'light');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    setIsDarkMode(prev => !prev);
  };

  const fetchOrderHistory = async () => {
    try {
      const response = await api.get('/wheelchair/orders');
      if (response.data.success) {
        setOrderHistory(response.data.data || []);
      }
    } catch (error) {
      console.error('Error fetching order history:', error);
    }
  };

  const fetchCurrentSubscriptions = async () => {
    try {
      const response = await api.get('/wheelchair/orders');
      const allOrders = response.data.data || [];
      // Filter for active/ongoing subscriptions (confirmed, processing, in-transit)
      const active = allOrders.filter(order => 
        ['confirmed', 'processing', 'in-transit'].includes(order.status) &&
        order.payment?.status === 'completed'
      );
      setCurrentSubscriptions(active);
    } catch (error) {
      console.error('Error fetching current subscriptions:', error);
    }
  };

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
    setDeliveryType(null); // Reset delivery type when plan changes
  };

  const handleDeliverySelect = (deliveryId) => {
    setDeliveryType(deliveryId);
  };

  const handleAddressChange = (e) => {
    const { name, value } = e.target;
    setAddressData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const calculateTotalPrice = () => {
    if (!selectedPlan || !deliveryType) return 0;
    
    const plan = subscriptionPlans.find(p => p.id === selectedPlan);
    const delivery = deliveryOptions.find(d => d.id === deliveryType);
    
    if (!plan || !delivery) return 0;
    
    const basePrice = deliveryType === 'urgent' ? plan.urgentPrice : plan.price;
    return basePrice + delivery.price;
  };

  const handleProceedToPayment = async () => {
    if (!selectedPlan || !deliveryType) {
      alert('Please select both a subscription plan and delivery option');
      return;
    }

    // Validate address - only street, city, and phone are required
    if (!addressData.street || !addressData.city || !addressData.phone) {
      alert('Please fill in all required address fields (Street, City, Contact Phone)');
      return;
    }

    setIsLoading(true);
    try {
      const orderData = {
        planId: selectedPlan,
        deliveryType: deliveryType,
        totalAmount: calculateTotalPrice(),
        plan: subscriptionPlans.find(p => p.id === selectedPlan),
        delivery: deliveryOptions.find(d => d.id === deliveryType),
        deliveryAddress: {
          street: addressData.street.trim(),
          city: addressData.city.trim(),
          state: addressData.state.trim(),
          zipCode: addressData.zipCode.trim(),
          country: addressData.country.trim(),
          phone: addressData.phone.trim()
        },
        customerPhone: addressData.phone.trim(),
        notes: addressData.notes.trim()
      };

      console.log('Creating order with data:', orderData);

      // Create order
      const response = await api.post('/wheelchair/create-order', orderData);
      
      console.log('Order creation response:', response.data);
      
      if (response.data.success) {
        // Redirect to payment gateway
        console.log('Redirecting to payment with orderId:', response.data.data._id);
        navigate('/wheelchair/payment', { 
          state: { 
            orderId: response.data.data._id,
            orderData: orderData
          } 
        });
      } else {
        alert('Failed to create order: ' + (response.data.message || 'Unknown error'));
      }
    } catch (error) {
      console.error('Error creating order:', error);
      console.error('Error response:', error.response?.data);
      console.error('Error status:', error.response?.status);
      
      let errorMessage = 'Failed to create order. Please try again.';
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.response?.status === 401) {
        errorMessage = 'Please log in to continue.';
      } else if (error.response?.status === 500) {
        errorMessage = 'Server error. Please try again later.';
      }
      
      alert(errorMessage);
    } finally {
      setIsLoading(false);
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending': return '⏳';
      case 'confirmed': return '✅';
      case 'processing': return '🔄';
      case 'in-transit': return '🚚';
      case 'delivered': return '🎉';
      case 'cancelled': return '❌';
      default: return '📋';
    }
  };

  return (
    <div className={`wheelchair-page ${isDarkMode ? 'dark-mode' : 'light-mode'}`}>
      {/* Top Right Buttons */}
      <div className="top-right-buttons">
        <button
          onClick={toggleDarkMode}
          className="dark-mode-toggle"
          aria-label={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
          title={isDarkMode ? 'Switch to Light Mode' : 'Switch to Dark Mode'}
        >
          {isDarkMode ? (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
            </svg>
          ) : (
            <svg className="toggle-icon" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
            </svg>
          )}
        </button>
        <button 
          onClick={() => navigate('/emergency')}
          className="back-button-top"
        >
          <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
          </svg>
          <span>Back</span>
        </button>
      </div>

      {/* Header */}
      <div className="wheelchair-header">
        <div className="container">
          <div className="header-layout">
            <div className="header-content">
              <div className="header-icon-wrapper">
                <div className="header-icon">🦽</div>
                <div className="header-icon-glow"></div>
              </div>
              <h1>Wheelchair Subscription</h1>
              <p>Reliable wheelchair rental service for your mobility needs</p>
            </div>
          </div>
        </div>
      </div>

      <div className="container">
        <div className="wheelchair-content">
          {/* Subscription Plans */}
          <div className="plans-section">
            <div className="section-header">
              <h2>Choose Your Subscription Plan</h2>
              <p>Select the plan that best fits your needs</p>
            </div>

            <div className="plans-grid">
              {subscriptionPlans.map((plan) => (
                <div 
                  key={plan.id}
                  className={`plan-card ${plan.color} ${selectedPlan === plan.id ? 'selected' : ''} ${plan.popular ? 'popular' : ''}`}
                  onClick={() => handlePlanSelect(plan.id)}
                >
                  {plan.popular && <div className="popular-badge">Most Popular</div>}
                  
                  <div className="plan-icon">{plan.icon}</div>
                  <h3>{plan.name}</h3>
                  <div className="plan-duration">{plan.duration}</div>
                  
                  <div className="plan-pricing">
                    <div className="price-label">Standard Delivery</div>
                    <div className="price">৳{plan.price.toLocaleString()}</div>
                    {plan.discount > 0 && (
                      <>
                        <div className="original-price">৳{plan.originalPrice.toLocaleString()}</div>
                        <div className="discount-badge">-{plan.discount}% OFF</div>
                      </>
                    )}
                  </div>

                  <div className="plan-pricing urgent">
                    <div className="price-label">Urgent Delivery</div>
                    <div className="price">৳{plan.urgentPrice.toLocaleString()}</div>
                    {plan.discount > 0 && (
                      <>
                        <div className="original-price">৳{plan.originalUrgentPrice.toLocaleString()}</div>
                        <div className="discount-badge">-{plan.discount}% OFF</div>
                      </>
                    )}
                  </div>

                  <div className="plan-features">
                    <ul>
                      {plan.features.map((feature, index) => (
                        <li key={index}>
                          <span className="feature-icon">✓</span>
                          {feature}
                        </li>
                      ))}
                    </ul>
                  </div>

                  <button 
                    className={`select-plan-btn ${selectedPlan === plan.id ? 'selected' : ''}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handlePlanSelect(plan.id);
                    }}
                  >
                    {selectedPlan === plan.id ? 'Selected' : 'Select Plan'}
                  </button>
                </div>
              ))}
            </div>
          </div>

          {/* Delivery Options */}
          {selectedPlan && (
            <div className="delivery-section">
              <div className="section-header">
                <h2>Choose Delivery Option</h2>
                <p>Select your preferred delivery method</p>
              </div>

              <div className="delivery-options">
                {deliveryOptions.map((option) => (
                  <div 
                    key={option.id}
                    className={`delivery-card ${deliveryType === option.id ? 'selected' : ''}`}
                    onClick={() => handleDeliverySelect(option.id)}
                  >
                    <div className="delivery-icon">{option.icon}</div>
                    <div className="delivery-content">
                      <h3>{option.name}</h3>
                      <p>{option.description}</p>
                      {option.price > 0 && (
                        <div className="delivery-price">+৳{option.price}</div>
                      )}
                    </div>
                    <div className="delivery-radio">
                      <input 
                        type="radio" 
                        checked={deliveryType === option.id}
                        onChange={() => handleDeliverySelect(option.id)}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Delivery Address */}
          {selectedPlan && deliveryType && (
            <div className="address-section">
              <div className="section-header">
                <h2>Delivery Address</h2>
                <p>Please provide your delivery address</p>
              </div>

              <div className="address-form">
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="street">Street Address *</label>
                    <input
                      type="text"
                      id="street"
                      name="street"
                      value={addressData.street}
                      onChange={handleAddressChange}
                      placeholder="House/Flat No., Road, Area"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="city">City *</label>
                    <input
                      type="text"
                      id="city"
                      name="city"
                      value={addressData.city}
                      onChange={handleAddressChange}
                      placeholder="City name"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="state">State/Division</label>
                    <input
                      type="text"
                      id="state"
                      name="state"
                      value={addressData.state}
                      onChange={handleAddressChange}
                      placeholder="State or Division (optional)"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="zipCode">ZIP Code</label>
                    <input
                      type="text"
                      id="zipCode"
                      name="zipCode"
                      value={addressData.zipCode}
                      onChange={handleAddressChange}
                      placeholder="Postal code (optional)"
                    />
                  </div>
                  <div className="form-group">
                    <label htmlFor="country">Country</label>
                    <input
                      type="text"
                      id="country"
                      name="country"
                      value={addressData.country}
                      onChange={handleAddressChange}
                      placeholder="Country"
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group">
                    <label htmlFor="phone">Contact Phone *</label>
                    <input
                      type="tel"
                      id="phone"
                      name="phone"
                      value={addressData.phone}
                      onChange={handleAddressChange}
                      placeholder="01XXXXXXXXX"
                      required
                    />
                  </div>
                </div>
                <div className="form-row">
                  <div className="form-group full-width">
                    <label htmlFor="notes">Delivery Instructions (Optional)</label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={addressData.notes}
                      onChange={handleAddressChange}
                      placeholder="Any special delivery instructions..."
                      rows="3"
                    />
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Order Summary */}
          {selectedPlan && deliveryType && (
            <div className="order-summary-section">
              <div className="section-header">
                <h2>Order Summary</h2>
              </div>

              <div className="order-summary-card">
                <div className="summary-row">
                  <span>Plan:</span>
                  <span>{subscriptionPlans.find(p => p.id === selectedPlan)?.name}</span>
                </div>
                <div className="summary-row">
                  <span>Delivery:</span>
                  <span>{deliveryOptions.find(d => d.id === deliveryType)?.name}</span>
                </div>
                <div className="summary-row">
                  <span>Base Price:</span>
                  <span>৳{(deliveryType === 'urgent' ? 
                    subscriptionPlans.find(p => p.id === selectedPlan)?.urgentPrice : 
                    subscriptionPlans.find(p => p.id === selectedPlan)?.price
                  ).toLocaleString()}</span>
                </div>
                {deliveryOptions.find(d => d.id === deliveryType)?.price > 0 && (
                  <div className="summary-row">
                    <span>Delivery Fee:</span>
                    <span>৳{deliveryOptions.find(d => d.id === deliveryType)?.price}</span>
                  </div>
                )}
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>৳{calculateTotalPrice().toLocaleString()}</span>
                </div>
              </div>

              <button 
                className="proceed-payment-btn"
                onClick={handleProceedToPayment}
                disabled={isLoading}
              >
                {isLoading ? 'Processing...' : 'Proceed to Payment'}
              </button>
            </div>
          )}

          {/* Current Subscriptions */}
          {currentSubscriptions.length > 0 && (
            <div className="current-subscriptions-section">
              <div className="section-header">
                <h2>Current Subscriptions</h2>
                <p>Your active wheelchair subscriptions</p>
              </div>

              <div className="subscriptions-grid">
                {currentSubscriptions.map((subscription) => (
                  <div key={subscription._id} className="subscription-card">
                    <div className="subscription-header">
                      <div className="subscription-icon">🦽</div>
                      <div className="subscription-info">
                        <h3>{subscription.plan?.name || 'Wheelchair'}</h3>
                        <p className="subscription-duration">{subscription.plan?.duration || 'N/A'}</p>
                      </div>
                    </div>
                    <div className="subscription-details">
                      <div className="detail-row">
                        <span>Delivery:</span>
                        <span>{subscription.delivery?.name || subscription.deliveryType}</span>
                      </div>
                      <div className="detail-row">
                        <span>Amount:</span>
                        <span className="amount">৳{subscription.totalAmount?.toLocaleString() || '0'}</span>
                      </div>
                      {subscription.orderId && (
                        <div className="detail-row">
                          <span>Order ID:</span>
                          <span className="order-id">{subscription.orderId}</span>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Order History */}
          <div className="history-section">
            <div className="section-header">
              <h2>Order History</h2>
              <button 
                className="toggle-history-btn"
                onClick={() => setShowHistory(!showHistory)}
              >
                {showHistory ? 'Hide History' : 'Show History'}
              </button>
            </div>

            {showHistory && (
              <div className="history-content">
                {orderHistory.length === 0 ? (
                  <div className="no-orders">
                    <p>No orders found. Create your first wheelchair subscription!</p>
                  </div>
                ) : (
                  <div className="orders-list">
                    {orderHistory.map((order) => (
                      <div key={order._id} className="order-item">
                        <div className="order-header">
                          <div className="order-id">Order #{order.orderId}</div>
                          <div className={`order-status ${order.status}`}>
                            {getStatusIcon(order.status)} {order.status}
                          </div>
                        </div>
                        <div className="order-details">
                          <div className="order-info">
                            <span><strong>Plan:</strong> {order.plan?.name || 'N/A'}</span>
                            <span><strong>Delivery:</strong> {order.delivery?.name || 'N/A'}</span>
                            <span><strong>Amount:</strong> ৳{order.totalAmount?.toLocaleString() || '0'}</span>
                          </div>
                          <div className="order-date">
                            Ordered: {new Date(order.createdAt).toLocaleDateString()}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default WheelchairPage;
